/**
 * Switchable "room" backdrops for the table. Each environment is either the
 * built-in procedural billiard hall (a gradient surround) or a real 360°
 * equirectangular photo shown as the scene background. The photos are CC0
 * panoramas from Poly Haven (https://polyhaven.com), streamed on demand and
 * cached by the caller; swap the URLs for your own hi-res equirectangular shots
 * (e.g. a genuine LA beach / Seoul skyline) to localise the scenery.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

let ktx2Loader: KTX2Loader | null = null;

/** Call once after the WebGLRenderer exists, so loadModel() can decode KTX2/Basis textures. */
export function initKTX2(renderer: THREE.WebGLRenderer): void {
  ktx2Loader = new KTX2Loader().setTranscoderPath('/basis/').detectSupport(renderer);
}

export type EnvId = 'hall' | 'beach' | 'billiardhall';

export interface EnvDef {
  readonly id: EnvId;
  /** Equirectangular 360° VR photo URL. */
  readonly panorama?: string;
  /** Equirectangular 360° VR video URL (looped, muted). */
  readonly video?: string;
  /** A 3D room/scene model (.glb/.gltf, e.g. modelled in Blender) placed around
   *  the table as the surround. */
  readonly model?: string;
  /** Up-axis the model was authored with. Blender's default glTF export is
   *  'y' (we rotate it to the game's z-up); pass 'z' for already-z-up models
   *  (e.g. our own tools/export_table.mjs output). Defaults to 'y'. */
  readonly modelUp?: 'y' | 'z';
  /** Uniform scale to apply to the model on load (metres). Defaults to 1. */
  readonly modelScale?: number;
  /** World-space position applied to the loaded model so a RAW Blender export lands
   *  aligned (play table at the origin) without recentring the file. Computed once
   *  from the table spot; stays valid as long as that spot doesn't move in Blender. */
  readonly modelOffset?: readonly [number, number, number];
  /** Node names to hide on load (e.g. the 'TableAnchor' placement helper). */
  readonly hideNodes?: readonly string[];
  /** Per-material overrides applied on load (so a raw export needs no editing):
   *  `color` = linear base colour, `emissive` = multiplier on emissive strength
   *  (use <1 to dim a baked lightmap). Keyed by material name. */
  readonly materialTweaks?: Record<string, { color?: readonly [number, number, number]; emissive?: number }>;
  /** If set, the game adds a real point light at every mesh whose name starts with
   *  this prefix — used to turn modelled ceiling-lamp panels into actual light
   *  sources (glTF can't carry Blender area lights, and emissive meshes don't cast
   *  light). e.g. 'Light_' for the billiard hall's lamp panels. */
  readonly lampPrefix?: string;
  // Omit panorama/video/model for the built-in procedural billiard hall.
}

/** Poly Haven tonemapped-JPG equirectangular 360° photos (CC0). */
const PH = 'https://dl.polyhaven.org/file/ph-assets/HDRIs/extra/Tonemapped%20JPG/';

export const ENVIRONMENTS: Record<EnvId, EnvDef> = {
  hall: { id: 'hall' }, // the dim procedural billiard room (always offline-safe)
  beach: { id: 'beach', panorama: PH + 'secluded_beach.jpg' }, // 360° photo: sunlit ocean beach
  // The full billiard hall modelled in Blender (public/env/billiard_hall.glb).
  // Bump ?v= after re-exporting the .glb to defeat the browser's HTTP cache.
  billiardhall: {
    id: 'billiardhall',
    model: import.meta.env.BASE_URL + 'env/billiard_hall.glb?v=15',
    modelUp: 'y',
    modelScale: 1,
    lampPrefix: 'Light_',
    // Auto-align the RAW export: no file recentre needed. (Re-derive only if the
    // table spot / 2nd-floor deck moves in Blender — see tools/import_hall.mjs math.)
    modelOffset: [-5.668, -0.056, -5.837],
    hideNodes: ['TableAnchor', 'TABLE_MARKER'],
    // Dark-bar look applied on load, so raw exports never need material editing.
    materialTweaks: {
      Carpet: { color: [0.008, 0.008, 0.01], emissive: 0.04 }, // near-black very-dark-gray carpet
      Wall: { color: [0.025, 0.025, 0.028], emissive: 0.15 },
      Ceiling: { color: [0.016, 0.016, 0.018], emissive: 0.15 },
      Facade: { color: [0.02, 0.02, 0.022] },
      Outdoor_Floor: { color: [0.022, 0.022, 0.025] },
    },
  },
};

export const ENV_IDS = Object.keys(ENVIRONMENTS) as EnvId[];
// The 3D-modelled hall is the default scene. If its .glb fails to load,
// applyModelEnvironment() falls back to the procedural room.
export const DEFAULT_ENV: EnvId = 'billiardhall';

/** Load an equirectangular 360° photo as a wrap-around background texture. */
export function loadPanorama(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (tex) => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        resolve(tex);
      },
      undefined,
      reject
    );
  });
}

/**
 * Start a looping, muted equirectangular 360° VR video and wrap it as a live
 * background texture. `crossOrigin = 'anonymous'` means the texture is only used
 * if the host sends CORS headers (otherwise the video errors out cleanly rather
 * than tainting WebGL). The caller owns play/pause of the returned element.
 */
export function loadPanoramaVideo(url: string): { texture: THREE.VideoTexture; video: HTMLVideoElement } {
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;
  const texture = new THREE.VideoTexture(video);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return { texture, video };
}

/**
 * Load a 3D room model (.glb/.gltf) as a surround for the table. Blender's
 * default glTF export is Y-up, so we rotate it into the game's z-up frame;
 * pass `up: 'z'` for models that are already z-up. The returned group is ready
 * to add to the scene; the caller owns add/remove and shadow toggles.
 *
 * `shadowRadius` opts a model into the cheap shadow policy — see applyShadowPolicy.
 * Without it, every mesh casts and receives (right for the small table model).
 *
 * Refraction (KHR_materials_transmission) is stripped from every model it loads;
 * see the note on that loop below for why it is not optional.
 */
export function loadModel(
  url: string,
  opts: {
    up?: 'y' | 'z';
    scale?: number;
    offset?: readonly [number, number, number];
    hide?: readonly string[];
    materialTweaks?: Record<string, { color?: readonly [number, number, number]; emissive?: number }>;
    shadowRadius?: number;
  } = {},
): Promise<THREE.Group> {
  const { up = 'y', scale = 1, offset, hide, materialTweaks, shadowRadius } = opts;
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    if (ktx2Loader) loader.setKTX2Loader(ktx2Loader);
    loader.load(
      url,
      (gltf) => {
        const root = gltf.scene;
        // Y-up (Blender default) → the game's z-up: tip the model upright.
        if (up === 'y') root.rotation.x = Math.PI / 2;
        if (scale !== 1) root.scale.setScalar(scale);
        if (offset) root.position.set(offset[0], offset[1], offset[2]); // align a raw export
        // Decorate meshes (hide helpers, shadows, dark-bar tweaks). Wrapped so a bad
        // node/material can NEVER reject the load — alignment must always apply, else
        // the game silently falls back to the bare procedural room ("first floor").
        const hideSet = hide ? new Set(hide) : null;
        root.traverse((o) => {
          try {
            if (hideSet && hideSet.has(o.name)) o.visible = false; // drop placement helpers
            const mesh = o as THREE.Mesh;
            if (!mesh.isMesh) return;
            // shadowRadius models get their flags from applyShadowPolicy() below,
            // once world matrices exist; everything else casts and receives.
            if (shadowRadius === undefined) {
              mesh.castShadow = true;
              mesh.receiveShadow = true;
            }
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            // Strip refraction, for the frame budget. ONE material with
            // `transmission > 0` anywhere in the scene makes WebGLRenderer draw
            // the WHOLE scene a second time every frame, into the transmission
            // render target that refraction samples as its backdrop. The cost is
            // not proportional to how much glass there is: three decorative props
            // are as expensive as a hall made of windows.
            //
            // Measured on billiard_hall.glb (M1, 2268x1473, vsync off): one window
            // pane and two drinks on the bar took draw calls 90 -> 177, triangles
            // 164,700 -> 327,864 and the frame 10.7ms -> 22.8ms — half the 60fps
            // budget. So every model loses transmission and keeps plain alpha
            // blending, which holds up for scenery a table's length away; a model
            // that genuinely needs refraction up close would need an opt-out here.
            for (const mat of mats) {
              const pm = mat as THREE.MeshPhysicalMaterial | undefined;
              if (!pm?.transmission) continue;
              // Stay see-through: transmission is roughly "how much shows
              // through", which is what a low alpha buys us for free.
              pm.opacity = Math.min(pm.opacity ?? 1, 1 - pm.transmission * 0.9);
              pm.transmission = 0;
              pm.transparent = true;
              pm.needsUpdate = true;
            }
            if (!materialTweaks) return;
            for (const mat of mats) {
              const sm = mat as THREE.MeshStandardMaterial | undefined;
              const tw = sm && materialTweaks[sm.name];
              if (!sm || !tw) continue;
              if (tw.color && sm.color) sm.color.setRGB(tw.color[0], tw.color[1], tw.color[2], THREE.LinearSRGBColorSpace);
              if (tw.emissive !== undefined && 'emissiveIntensity' in sm) sm.emissiveIntensity = (sm.emissiveIntensity ?? 1) * tw.emissive;
            }
          } catch {
            /* a single bad node/material shouldn't break the whole hall */
          }
        });
        if (shadowRadius !== undefined) applyShadowPolicy(root, shadowRadius);
        resolve(root);
      },
      undefined,
      reject
    );
  });
}

/**
 * Cheap shadows for a big static surround model.
 *
 * The only shadow-casting light is one table spotlight (render/Lighting.ts) hung
 * ~1.7 m over the table with a short throw — its shadow camera reaches a few
 * metres around the table and nothing else. So for a whole modelled hall:
 *
 *  - Nothing in the model CASTS. The furniture is outside the lamp's throw, so
 *    it was rendering into the shadow map to contribute nothing, once per frame.
 *  - Only geometry within `radius` of the table RECEIVES. `receiveShadow` isn't
 *    free bookkeeping: it forces that material into the PCFSoft shader variant,
 *    which costs several extra texture taps per fragment. Off the floor around
 *    the table there is no shadow to sample, so it is pure waste.
 *
 * Measured on a mesh's world bounding sphere, so a mesh that merely reaches into
 * the lit area still receives.
 */
export function applyShadowPolicy(root: THREE.Object3D, radius: number): void {
  root.updateMatrixWorld(true);
  const centre = new THREE.Vector3();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = false;
    let near = false;
    try {
      const geo = mesh.geometry;
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      const bs = geo.boundingSphere;
      if (bs) {
        centre.copy(bs.center).applyMatrix4(mesh.matrixWorld);
        // the local sphere radius is unscaled; grow it by the largest world axis scale
        const s = mesh.matrixWorld.getMaxScaleOnAxis();
        near = Math.hypot(centre.x, centre.y) - bs.radius * s <= radius;
      }
    } catch {
      /* unreadable geometry: fall through to "doesn't receive" */
    }
    mesh.receiveShadow = near;
  });
}

/**
 * Collapse a static model's meshes into one mesh per material.
 *
 * The hall model is ~620 separate meshes but only ~26k triangles and ~70
 * materials, so its cost is almost entirely per-draw-call overhead rather than
 * geometry — and it pays that overhead twice on frames that also rebuild the
 * shadow map. Baking each mesh's transform into its geometry and merging by
 * material turns ~620 draws into ~70 with identical pixels.
 *
 * What is deliberately left alone:
 *  - anything named in `keep` (lamp panels that carry a real light) — those need
 *    their own transform later
 *  - invisible meshes (hidden placement helpers), which must not be baked in
 *  - skinned/morphed meshes, whose animation lives in the node transform
 *  - single-mesh material groups, where merging would buy nothing
 *
 * Merged geometry is placed in root-local space and parented to `root`, so the
 * caller can still add/remove the model as one object. Per-mesh frustum culling
 * is lost, which is the right trade at this triangle count.
 *
 * Returns [before, after] draw-call counts for logging.
 */
export function mergeStatic(root: THREE.Object3D, keep: (mesh: THREE.Mesh) => boolean): [number, number] {
  root.updateMatrixWorld(true);
  const toRootLocal = root.matrixWorld.clone().invert();

  // group by material identity AND attribute signature — mergeGeometries rejects
  // a group whose members don't share the exact same set of attributes
  const groups = new Map<string, { mat: THREE.Material; meshes: THREE.Mesh[] }>();
  let before = 0;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.visible) return;
    before++;
    if (keep(mesh)) return;
    if ((mesh as THREE.SkinnedMesh).isSkinnedMesh) return;
    if (mesh.morphTargetInfluences?.length) return;
    if (Array.isArray(mesh.material)) return; // multi-material mesh: has its own draw groups
    const mat = mesh.material;
    if (!mat) return;
    const sig = mat.uuid + '|' + Object.keys(mesh.geometry.attributes).sort().join(',');
    let g = groups.get(sig);
    if (!g) groups.set(sig, (g = { mat, meshes: [] }));
    g.meshes.push(mesh);
  });

  const merged: THREE.Mesh[] = [];
  const consumed: THREE.Mesh[] = [];
  for (const { mat, meshes } of groups.values()) {
    if (meshes.length < 2) continue; // nothing to gain
    const geos: THREE.BufferGeometry[] = [];
    for (const mesh of meshes) {
      const g = mesh.geometry.clone();
      g.applyMatrix4(toRootLocal.clone().multiply(mesh.matrixWorld)); // world → root-local
      geos.push(g);
    }
    let out: THREE.BufferGeometry | null = null;
    try {
      out = mergeGeometries(geos, false);
    } catch {
      out = null; // incompatible group: leave those meshes as they were
    }
    for (const g of geos) g.dispose();
    if (!out) continue;
    const m = new THREE.Mesh(out, mat);
    // one merged mesh inherits the flags of its parts; they were uniform per
    // material in practice, and `some` keeps a shadow rather than dropping one
    m.castShadow = meshes.some((x) => x.castShadow);
    m.receiveShadow = meshes.some((x) => x.receiveShadow);
    m.name = 'merged:' + (mat.name || mat.uuid.slice(0, 8));
    m.matrixAutoUpdate = false;
    merged.push(m);
    consumed.push(...meshes);
  }

  // only mutate the tree once every merge has succeeded
  for (const mesh of consumed) mesh.removeFromParent();
  for (const m of merged) root.add(m);

  return [before, before - consumed.length + merged.length];
}
